/*
 * Brick 4 (N7) — Power BI-inspired home-page visualisations
 * ---------------------------------------------------------------------------
 * All cards read from the new /api/analytics/{hourly-heatmap,waterfall,hisab-funnel}
 * endpoints. Each one is self-fetching so the parent Home.jsx doesn't have to
 * juggle 4 parallel requests. Loading states are compact skeletons.
 *
 * The Monthly P&L PDF button that used to live on the deleted Deep-dive
 * Dashboard is exposed here as `MonthlyPnlBar` so dealers can still download.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip,
  FunnelChart, Funnel, LabelList,
} from "recharts";
import { AlertTriangle, BarChart3, Download, Filter, Fuel, IndianRupee, TrendingDown, TrendingUp, Users, Calendar } from "lucide-react";
import { api, downloadPdf, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
const rupee = (v) =>
  v == null ? "-" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const rupeeCompact = (v) => {
  const n = Math.abs(Number(v || 0));
  if (n >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `${(v / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(Math.round(v || 0));
};

// ============================================================================
// Monthly P&L bar — month picker + download-PDF button (moved off Deep-dive)
// ============================================================================
export function MonthlyPnlBar() {
  const { t: __t } = useTranslation();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadPdf(`/analytics/pnl/pdf?month=${month}`, { filename: `pnl-${month}.pdf` });
      toast.success(__t("components.homevisuals.p_l_pdf_downloaded"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm"
      data-testid="home-monthly-pnl-bar"
    >
      <div className="flex items-center gap-2 text-xs">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="font-bold text-foreground">Monthly P&amp;L</span>
        <span className="text-muted-foreground">— download the full report for any month</span>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-8 w-36 text-xs"
          data-testid="home-pnl-month"
        />
        <Button size="sm" variant="outline" onClick={download} disabled={busy} data-testid="home-pnl-download">
          <Download className="mr-1 h-3.5 w-3.5" /> PDF
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Waterfall — Sales → -Expenses → -Shortage → +Surplus → Net
// ============================================================================
export function WaterfallCard({ period = "30d" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    api.get(`/analytics/waterfall?period=${period}`)
      .then((r) => !dead && setData(r.data))
      .catch(() => !dead && setData({ steps: [] }));
    return () => { dead = true; };
  }, [period]);

  const bars = useMemo(() => {
    if (!data?.steps) return [];
    // Compute the running base so recharts can render a "floating" bar look
    let base = 0;
    return data.steps.map((s, i) => {
      const v = Number(s.value || 0);
      const isTotal = s.kind === "total";
      if (isTotal) {
        return { ...s, base: 0, height: v, top: v };
      }
      const top = base + v;
      const b = v >= 0 ? base : top;
      const h = Math.abs(v);
      const row = { ...s, base: b, height: h, top: b + h };
      base = top;
      return row;
    });
  }, [data]);

  if (!data) {
    return <SkeletonCard height={280} testId="home-waterfall-loading" />;
  }
  const hasData = bars.some((b) => Math.abs(b.height) > 0);

  return (
    <Card className="border-border" data-testid="home-waterfall">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4 text-primary" /> Cash waterfall
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {periodLabel(period)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyChart msg="No sales in this period yet." />
        ) : (
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars} margin={{ top: 12, right: 0, bottom: 0, left: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={rupeeCompact} width={48} />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-md border border-border bg-card px-2 py-1 text-[11px] shadow-md">
                        <div className="font-bold">{d.label}</div>
                        <div className="font-mono text-muted-foreground">{rupee(d.value)}</div>
                      </div>
                    );
                  }}
                />
                {/* Invisible base — floating effect */}
                <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
                <Bar dataKey="height" stackId="w" radius={[4, 4, 0, 0]}>
                  {bars.map((b) => (
                    <Cell
                      key={b.key}
                      fill={
                        b.kind === "total"
                          ? "#0ea5e9" // sky
                          : b.kind === "negative"
                          ? "#ef4444" // red
                          : "#10b981" // emerald
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-2 text-[11px] text-muted-foreground">
          <span className="font-bold text-foreground">{rupee(bars[bars.length - 1]?.top || 0)}</span>
          &nbsp;net cash after expenses &amp; shortage — before fuel wholesale.
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// HISAB flow funnel — Opened → Submitted → Approved (+ Rejected leak)
// ============================================================================
export function HisabFunnelCard({ period = "30d" }) {
  const { t: __t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    api.get(`/analytics/hisab-funnel?period=${period}`)
      .then((r) => !dead && setData(r.data))
      .catch(() => !dead && setData({ steps: [], rejected: 0 }));
    return () => { dead = true; };
  }, [period]);

  const funnelData = useMemo(() => {
    if (!data?.steps) return [];
    const palette = ["#8b5cf6", "#0ea5e9", "#10b981"]; // violet, sky, emerald
    return data.steps.map((s, i) => ({
      name: s.label,
      value: s.count,
      fill: palette[i] || "#94a3b8",
    }));
  }, [data]);

  if (!data) return <SkeletonCard height={280} testId="home-funnel-loading" />;
  const hasData = funnelData.some((d) => d.value > 0);
  const drop = data.steps.length >= 2
    ? Math.max(0, data.steps[0].count - data.steps[data.steps.length - 1].count)
    : 0;

  return (
    <Card className="border-border" data-testid="home-hisab-funnel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Filter className="h-4 w-4 text-primary" /> HISAB flow
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {periodLabel(period)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyChart msg="No HISABs in this period yet." />
        ) : (
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <FunnelChart>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-md border border-border bg-card px-2 py-1 text-[11px] shadow-md">
                        <div className="font-bold">{d.name}</div>
                        <div className="font-mono text-muted-foreground">{d.value} shifts</div>
                      </div>
                    );
                  }}
                />
                <Funnel dataKey="value" data={funnelData} isAnimationActive>
                  <LabelList
                    position="right"
                    dataKey="name"
                    fill="hsl(var(--foreground))"
                    stroke="none"
                    fontSize={11}
                  />
                  <LabelList
                    position="center"
                    dataKey="value"
                    fill="#fff"
                    stroke="none"
                    fontSize={13}
                    fontWeight={700}
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between text-[11px]">
          <span className="text-muted-foreground">
            {drop > 0 ? (
              <>
                <span className="font-bold text-foreground">{drop}</span> shift{drop === 1 ? "" : "s"} dropped along the way
              </>
            ) : "Every opened HISAB was approved 🎉"}
          </span>
          {data.rejected > 0 && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700" title={__t("components.homevisuals.shifts_that_reached_submitted_but_were_r")}>
              {data.rejected} rejected
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Hour × Weekday heatmap — busiest sales windows
// ============================================================================
export function HourlyHeatmapCard({ days = 30 }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    api.get(`/analytics/hourly-heatmap?days=${days}`)
      .then((r) => !dead && setData(r.data))
      .catch(() => !dead && setData({ matrix: [], weekdays: [], peak: 0 }));
    return () => { dead = true; };
  }, [days]);

  if (!data) return <SkeletonCard height={220} testId="home-heatmap-loading" />;
  const peak = data.peak || 0;
  const hasData = peak > 0;

  const cellColor = (v) => {
    if (!peak || v <= 0) return "hsl(var(--secondary))";
    const t = Math.min(1, v / peak);
    // Amber ramp — start light, end deep amber
    const light = 92 - Math.round(t * 50); // 92% → 42% lightness
    return `hsl(38 92% ${light}%)`;
  };

  return (
    <Card className="border-border" data-testid="home-heatmap">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-4 w-4 text-primary" /> Sales heatmap
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            last {data.days || days} days · IST
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyChart msg="No sales in the past month to plot." />
        ) : (
          <div className="overflow-x-auto">
            {/* 25-col grid: 1 label col + 24 hour cols */}
            <div className="min-w-[560px]">
              {/* Hour header */}
              <div className="grid grid-cols-[36px_repeat(24,1fr)] gap-0.5 text-[9px] text-muted-foreground">
                <div />
                {Array.from({ length: 24 }).map((_, h) => (
                  <div key={h} className="text-center">{h % 3 === 0 ? h : ""}</div>
                ))}
              </div>
              {(data.weekdays || []).map((wd, wi) => (
                <div key={wd} className="mt-0.5 grid grid-cols-[36px_repeat(24,1fr)] gap-0.5">
                  <div className="text-[10px] font-bold text-muted-foreground">{wd}</div>
                  {(data.matrix[wi] || []).map((v, hi) => (
                    <div
                      key={hi}
                      className="h-4 rounded-[2px] transition-transform hover:scale-125 hover:z-10 hover:ring-1 hover:ring-primary"
                      style={{ backgroundColor: cellColor(v) }}
                      title={`${wd} ${hi}:00 — ${rupee(v)}`}
                      data-testid={`heatmap-cell-${wi}-${hi}`}
                    />
                  ))}
                </div>
              ))}
              {/* Legend */}
              <div className="mt-3 flex items-center gap-1 text-[10px] text-muted-foreground">
                Low
                <div className="ml-1 flex gap-0.5">
                  {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t) => (
                    <div
                      key={t}
                      className="h-2 w-4 rounded-[2px]"
                      style={{ backgroundColor: cellColor(peak * t) }}
                    />
                  ))}
                </div>
                High · peak {rupee(peak)}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Top nozzles — mirrors the card from the deleted Deep-dive Dashboard
// ============================================================================
export function TopNozzlesCard({ nozzles = [] }) {
  const { t: __t } = useTranslation();
  return (
    <Card className="border-border" data-testid="home-top-nozzles-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Fuel className="h-4 w-4 text-primary" /> Top nozzles
        </CardTitle>
      </CardHeader>
      <CardContent>
        {nozzles.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">{__t("manager.no_nozzle_activity")}</div>
        ) : (
          nozzles.map((n) => (
            <div
              key={`${n.machine}-${n.nozzle}`}
              className="flex items-center justify-between border-b py-2 text-sm last:border-0"
              data-testid={`home-top-nozzle-${n.machine}-${n.nozzle}`}
            >
              <div className="min-w-0">
                <div className="font-semibold truncate">{n.machine} · {n.nozzle}</div>
                <div className="text-xs text-muted-foreground">{n.product} · {Number(n.litres || 0).toLocaleString("en-IN")} L</div>
              </div>
              <div className="font-mono text-sm font-bold">{rupee(n.amount)}</div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Staff performance — mirrors the card from the deleted Deep-dive Dashboard
// ============================================================================
export function StaffPerformanceCard({ staff = [] }) {
  const { t: __t } = useTranslation();
  return (
    <Card className="border-border" data-testid="home-staff-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-primary" /> Staff performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        {staff.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">{__t("components.homevisuals.no_shift_activity")}</div>
        ) : (
          staff.map((s) => (
            <div key={s.user_id} className="border-b py-2 text-sm last:border-0" data-testid={`home-staff-${s.user_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold truncate">{s.user_name}</div>
                <div className="font-mono">{rupee(s.sales)}</div>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-xs text-muted-foreground">
                <span>{s.hisabs} shift{s.hisabs === 1 ? "" : "s"}</span>
                <span className={s.shortage > 0 ? "font-semibold text-red-600" : "text-emerald-600"}>
                  {s.shortage > 0 ? `Shortage ${rupee(s.shortage)}` : "No shortage"}
                </span>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function SkeletonCard({ height = 240, testId }) {
  const { t: __t } = useTranslation();
  return (
    <Card className="border-border" data-testid={testId}>
      <CardHeader><CardTitle className="text-base">{__t("common.loading")}</CardTitle></CardHeader>
      <CardContent>
        <div className="animate-pulse rounded-md bg-secondary" style={{ height }} />
      </CardContent>
    </Card>
  );
}

function EmptyChart({ msg }) {
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-1 text-center text-xs text-muted-foreground">
      <AlertTriangle className="h-4 w-4" />
      {msg}
    </div>
  );
}

function periodLabel(p) {
  if (p === "today") return "Today";
  if (p === "yesterday") return "Yesterday";
  if (p === "7d") return "7 days";
  if (p === "30d") return "30 days";
  if (p === "90d") return "90 days";
  return p;
}
